import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Polyline } from "react-leaflet";
import L from "leaflet";
import { Zap, MessageCircleHeart, Mic, Send, BookOpen, Palmtree, UserRound, Bell, Maximize2, Minimize2, ChevronDown, FileText, Images } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, API_BASE, thumbUrl } from "../lib/api";
import { useGuestLoad } from "../lib/useGuestLoad";
import { GuestRetryScreen } from "../components/GuestRetryScreen";
import { VoiceRecorder } from "../components/VoiceRecorder";
import { DayComments } from "../components/DayComments";
import { EntryReactions } from "../components/EntryReactions";
import { PhotoPins } from "../components/PhotoPins";
import { DrivenRoute, TripProgressPanel } from "../components/RouteProgress";
import { DrivingNowBanner } from "../components/DrivingNowBanner";
import { Lightbox } from "../components/Lightbox";
import { JournalPhotoView } from "../components/JournalPhotoView";
import { SmartImg } from "../components/SmartImg";
import { VideoEmbed } from "../components/VideoEmbed";
import { SuggestionBox } from "../components/SuggestionBox";
import { LangSwitcher } from "../components/LangSwitcher";
import { useJournalLang } from "../lib/useJournalLang";
import { makeT } from "../lib/journalI18n";
import { usePageMeta } from "../lib/seo";
import { CARTO_LIGHT } from "../lib/mapTiles";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";

const LiveBar = ({ progress, slim = false, t, locale = "nl-BE" }) => {
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  if (!progress?.position || !progress.started || progress.finished) return null;
  const live = progress.source === "live";
  const fresh = live && progress.position_ts && Date.now() - new Date(progress.position_ts).getTime() <= 15 * 60 * 1000;
  const eta = progress.eta;
  return (
    <div data-testid="journal-live-bar"
      className={slim
        ? `mt-2.5 rounded-xl px-3 py-2 flex items-center gap-2 text-xs ${live ? "bg-[#16A34A]/10 text-foreground" : "bg-secondary/70 text-muted-foreground"}`
        : `rounded-2xl px-4 py-3 flex items-center gap-3 text-sm ${live ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground"}`}>
      <span className={`${slim ? "w-2 h-2" : "w-2.5 h-2.5"} rounded-full shrink-0 ${live ? `${slim ? "bg-[#16A34A]" : "bg-[#74C69D]"} animate-pulse` : "bg-muted-foreground/50"}`} />
      {live ? (
        eta ? (
          <span className="min-w-0">
            <span className="font-bold">{fresh ? t("nowDriving") : t("onderwegSignal", { time: fmtTime(progress.position_ts) })}</span> — {t("still")} <span className="font-mono font-bold">{Math.round(eta.remaining_km ?? 0)} km</span> · {t("arrival")}<span className="font-mono font-bold">{fmtTime(eta.time)}</span>{eta.place ? <> {t("at")} <span className="font-semibold">{eta.place}</span></> : null}
          </span>
        ) : (
          <span><span className="font-bold">{fresh ? t("livePosition") : t("nowDriving")}</span>{progress.position_ts ? ` — ${fresh ? t("lastUpdate", { time: fmtTime(progress.position_ts) }) : t("lastSignal", { time: fmtTime(progress.position_ts) })}` : ""}</span>
        )
      ) : (
        <span>{t("plannedPos")}{eta ? <> — {t("expected")}<span className="font-mono font-bold">{fmtTime(eta.time)}</span>{eta.place ? ` ${t("inWord")} ${eta.place}` : ""}</> : ` — ${t("noGpsToday")}`}</span>
      )}
    </div>
  );
};

export default function JournalPage() {
  const { token } = useParams();
  const { data: journal, error, load } = useGuestLoad(`/journal/${token}`);
  const [guestName, setGuestName] = useState(() => localStorage.getItem("vr_guest_name") || "");
  const [guestEmail, setGuestEmail] = useState(() => localStorage.getItem("vr_guest_email") || "");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const { lang, setLang, t, locale } = useJournalLang();
  const [tr, setTr] = useState(null);
  const [translating, setTranslating] = useState(false);
  usePageMeta(journal ? `Reisdagboek ${journal.trip_name} | VoltRoute` : undefined, journal ? `Volg de elektrische roadtrip "${journal.trip_name}" van ${journal.origin} naar ${journal.destination} — met foto's, verslagen en reacties.` : undefined);

  useEffect(() => { if (journal) setGuestName((n) => n || journal.guest_name); }, [journal]);

  useEffect(() => {
    if (lang === "nl" || !journal) { setTr(null); return; }
    let stop = false;
    setTranslating(true);
    api.get(`/journal/${token}/translations`, { params: { lang }, timeout: 120000 })
      .then((r) => { if (!stop) setTr(r.data); })
      .catch(() => { if (!stop) toast.error(makeT(lang)("translateFail")); })
      .finally(() => { if (!stop) setTranslating(false); });
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, journal, token]);

  const identityOk = guestName.trim().length > 0 && guestEmail.includes("@");
  const saveIdentity = () => {
    localStorage.setItem("vr_guest_name", guestName.trim());
    localStorage.setItem("vr_guest_email", guestEmail.trim());
  };

  const [subscribed, setSubscribed] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [mapFull, setMapFull] = useState(false);
  const [storyOpen, setStoryOpen] = useState(false);
  const [identityOpen, setIdentityOpen] = useState(() => {
    const n = (localStorage.getItem("vr_guest_name") || "").trim();
    const e = localStorage.getItem("vr_guest_email") || "";
    return !(n && e.includes("@"));
  });
  const [liveProgress, setLiveProgress] = useState(null);
  const [lb, setLb] = useState(null);
  const [journalView, setJournalView] = useState("log");
  const progress = liveProgress || journal?.progress;
  useEffect(() => {
    if (!journal?.progress?.total_km || journal.progress.finished) return;
    let stop = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get(`/journal/${token}/live`);
        if (!stop && data?.position) setLiveProgress(data);
      } catch { /* stil — volgende poll probeert opnieuw */ }
    };
    const iv = setInterval(tick, 30000);
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journal?.progress?.total_km, journal?.progress?.finished, token]);
  const mapRef = useRef(null);
  const toggleMapFull = () => {
    const next = !mapFull;
    setMapFull(next);
    setTimeout(() => {
      const m = mapRef.current;
      if (!m) return;
      m.invalidateSize();
      if (next) m.scrollWheelZoom.enable(); else m.scrollWheelZoom.disable();
    }, 250);
  };
  useEffect(() => {
    if (!mapFull) return;
    const onKey = (e) => { if (e.key === "Escape") toggleMapFull(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapFull]);
  useEffect(() => {
    if (!identityOk) return;
    api.get(`/journal/${token}/subscription`, { params: { email: guestEmail.trim().toLowerCase() } })
      .then((r) => setSubscribed(r.data.subscribed))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityOk, token]);
  const toggleSubscribe = async () => {
    if (!identityOk) {
      toast.error(t("fillIdentity"));
      return;
    }
    setSubBusy(true);
    try {
      const { data } = await api.post(`/journal/${token}/${subscribed ? "unsubscribe" : "subscribe"}`, { email: guestEmail.trim().toLowerCase(), name: guestName.trim(), lang });
      saveIdentity();
      if (!subscribed && data?.pending) {
        toast.success(t("subPending"), { duration: 8000 });
      } else {
        setSubscribed(!subscribed);
        toast.success(subscribed ? t("subUnsubbed") : t("subSubbed"));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubBusy(false);
    }
  };

  const submitComment = (dayNumber, entryId) => async (commentText, parentId) => {
    await api.post(`/journal/${token}/comments`, {
      text: commentText, author_name: guestName.trim(), author_email: guestEmail.trim(),
      day_number: dayNumber, entry_id: entryId || null, parent_id: parentId || null,
    });
    saveIdentity();
    toast.success(t("commentPosted"));
    load();
  };

  const reactComment = async (commentId, emoji) => {
    if (!identityOk) return toast.error(t("fillIdentityWho"));
    await api.post(`/journal/${token}/comment-reactions`, {
      comment_id: commentId, emoji, author_name: guestName.trim(), author_email: guestEmail.trim(),
    });
    saveIdentity();
    load();
  };

  const sendText = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      await api.post(`/journal/${token}/feedback`, { text, author_name: guestName || "" });
      toast.success(t("feedbackSent"));
      setText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const sendVoice = async (blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "feedback.webm");
    fd.append("author_name", guestName || "");
    try {
      await api.post(`/journal/${token}/feedback/voice`, fd);
      toast.success(t("voiceSent"));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (error === "transient" && !journal) return <GuestRetryScreen onRetry={load} />;

  if (error === "invalid") {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 text-center" data-testid="journal-not-found">
        <h1 className="font-heading font-extrabold text-2xl mb-3">{t("linkDeadTitle")}</h1>
        <p className="text-muted-foreground mb-6">{t("linkDeadBody")}</p>
        <Link to="/" className="rounded-full bg-primary hover:bg-[#1B4332] text-primary-foreground px-6 py-3 text-sm font-semibold transition-[background-color] duration-200">{t("planOwn")}</Link>
      </div>
    );
  }

  if (!journal) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const story = journal.story;
  const logs = journal.log_entries || [];
  const feedback = journal.feedback || [];
  const comments = journal.comments || [];
  const reactions = journal.reactions || [];
  const days = journal.days || [];
  const mediaUrl = (id) => `${API_BASE}/trips/${journal.trip_id}/photos/${id}?magic=${token}`;
  const entryText = (e) => (tr?.entries?.[e.id]) || e.text;

  const groups = days.map((d) => ({ day: d, entries: [] }));
  const other = [];
  logs.forEach((e) => {
    let g = e.day_number != null ? groups.find((x) => x.day.day_number === e.day_number) : null;
    if (!g) g = groups.find((x) => x.day.date === (e.timestamp || "").slice(0, 10));
    (g ? g.entries : other).push(e);
  });
  groups.forEach((g) => g.entries.sort((a, b) => (a.timestamp || "").localeCompare(b.timestamp || "")));
  const commentReactions = journal.comment_reactions || [];
  const dayComments = (n) => comments.filter((c) => c.day_number === n && !c.entry_id);
  const entryComments = (eid) => comments.filter((c) => c.entry_id === eid);
  const visibleGroups = groups.filter((g) => g.entries.length > 0 || dayComments(g.day.day_number).length > 0).reverse();
  const dayLabel = (day) => day.type === "stay" ? t("stayIn", { place: day.end }) : `${day.start} → ${day.end}`;

  const entryImageIds = (e) => [
    ...(e.photo_id ? [e.photo_id] : []),
    ...(e.media || []).filter((m) => m.content_type?.startsWith("image")).map((m) => m.id),
  ];
  const openLb = (e, id) => {
    const ids = entryImageIds(e);
    setLb({ urls: ids.map(mediaUrl), index: Math.max(0, ids.indexOf(id)) });
  };

  const renderEntry = (e) => (
    <div key={e.id} className="bg-card rounded-2xl p-4">
      {e.photo_id && <SmartImg src={thumbUrl(mediaUrl(e.photo_id))} retryLabel={t("photoRetry")} alt="Reisfoto" loading="lazy" decoding="async" onClick={() => openLb(e, e.photo_id)} className="rounded-xl mb-3 w-full max-h-64 object-cover cursor-zoom-in" />}
      {(e.media || []).filter((m) => m.content_type?.startsWith("image")).map((m) => (
        <SmartImg key={m.id} src={thumbUrl(mediaUrl(m.id))} retryLabel={t("photoRetry")} alt="Reisfoto" loading="lazy" decoding="async" onClick={() => openLb(e, m.id)} className="rounded-xl mb-3 w-full max-h-64 object-cover cursor-zoom-in" />
      ))}
      {(e.media || []).filter((m) => m.content_type?.startsWith("video")).map((m) => (
        <video key={m.id} controls preload="metadata" className="rounded-xl mb-3 w-full max-h-72 bg-black">
          <source src={mediaUrl(m.id)} type={m.content_type} />
        </video>
      ))}
      {(e.media || []).filter((m) => m.content_type === "application/pdf").map((m) => (
        <a key={m.id} href={mediaUrl(m.id)} target="_blank" rel="noreferrer" data-testid={`journal-doc-${m.id}`}
          className="flex items-center gap-2.5 bg-secondary rounded-xl px-3 py-2.5 mb-3 hover:bg-secondary/70 transition-[background-color] duration-200">
          <FileText className="w-5 h-5 text-red-500 shrink-0" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold truncate">{m.filename || "Document.pdf"}</span>
            <span className="block text-[11px] text-muted-foreground">PDF{m.size ? ` · ${m.size >= 1024 * 1024 ? `${(m.size / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(m.size / 1024))} KB`}` : ""}</span>
          </span>
        </a>
      ))}
      {e.video && <VideoEmbed video={e.video} testId={`journal-video-${e.id}`} />}
      {e.text && <p className="text-sm leading-relaxed">{entryText(e)}</p>}
      <EntryReactions reactions={reactions.filter((r) => r.entry_id === e.id)} entryId={e.id}
        myEmail={guestEmail.trim().toLowerCase()}
        onToggle={async (emoji) => {
          if (!identityOk) return toast.error(t("fillIdentityWho"));
          await api.post(`/journal/${token}/reactions`, { entry_id: e.id, emoji, author_name: guestName.trim(), author_email: guestEmail.trim() });
          saveIdentity();
          load();
        }} />
      <p className="text-xs text-muted-foreground mt-2 font-mono flex items-center gap-1">
        {e.source === "voice" && <Mic className="w-3 h-3" />}{e.location_name && `${e.location_name} · `}
        {new Date(e.timestamp).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
      </p>
      <DayComments comments={entryComments(e.id)} reactions={commentReactions} idKey={`e${e.id}`}
        myEmail={guestEmail.trim().toLowerCase()} onReact={reactComment} prominent t={t} locale={locale}
        onSubmit={submitComment(e.day_number ?? null, e.id)}
        label={t("comments")}
        placeholder={t("entryCommentsPh")}
        disabledHint={identityOk ? null : t("commentHint")} />
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="journal-page">
      {lb && <Lightbox urls={lb.urls} index={lb.index} onClose={() => setLb(null)} />}
      <header className="glass-panel border-b border-border/60 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="w-9 h-9 rounded-full bg-primary flex items-center justify-center shrink-0">
              <Zap className="w-5 h-5 text-primary-foreground" />
            </span>
            <div className="min-w-0">
              <h1 className="font-heading font-extrabold text-base tracking-tight truncate">{journal.trip_name}</h1>
              <p className="text-xs text-muted-foreground truncate">{journal.origin} → {journal.destination} · {journal.start_date} t/m {journal.end_date}</p>
            </div>
          </div>
          <LangSwitcher lang={lang} setLang={setLang} translating={translating} />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {(() => {
          const d = (journal.days || []).find((x) => x.day_number === progress?.current_day_number);
          const dl = d ? `${t("day")} ${d.day_number}: ${(d.start || "").split(",")[0]} → ${(d.end || "").split(",")[0]}` : "";
          return (
            <DrivingNowBanner progress={progress} dayLabel={dl} t={t} locale={locale}
              onFollow={() => {
                document.querySelector('[data-testid="journal-map"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                const p = progress?.position;
                if (p && mapRef.current) setTimeout(() => mapRef.current.flyTo([p.lat, p.lon], 11, { duration: 1.2 }), 400);
              }} />
          );
        })()}
        <div className="bg-primary/5 rounded-2xl p-5">
          <p className="font-heading font-bold text-lg" data-testid="journal-greeting">{t("greeting", { name: journal.guest_name })}</p>
          <p className="text-sm text-muted-foreground mt-1">{t("greetingSub")}</p>
        </div>

        {identityOk && !identityOpen ? (
          <div className="bg-card rounded-2xl px-4 py-2.5 flex items-center gap-2.5" data-testid="journal-identity-card">
            <UserRound className="w-4 h-4 text-[#B5838D] shrink-0" />
            <p className="text-sm min-w-0 truncate" data-testid="journal-identity-collapsed">{t("reactAs")} <span className="font-semibold">{guestName}</span></p>
            <button data-testid="journal-identity-edit" onClick={() => setIdentityOpen(true)}
              className="text-xs font-semibold text-primary hover:text-[#1B4332] shrink-0 transition-[color] duration-200">{t("edit")}</button>
            <button data-testid="journal-subscribe-toggle" onClick={toggleSubscribe} disabled={subBusy}
              title={subscribed ? t("subOnTitle") : t("subOffTitle")}
              className={`ml-auto shrink-0 w-9 h-9 rounded-full flex items-center justify-center disabled:opacity-50 transition-[background-color,color] duration-200 ${subscribed ? "bg-primary/10 text-primary" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>
              <Bell className={`w-4 h-4 ${subscribed ? "fill-current" : ""}`} />
            </button>
          </div>
        ) : (
        <div className="bg-card rounded-2xl p-5 space-y-3" data-testid="journal-identity-card">
          <div className="flex items-center gap-2">
            <p className="font-heading font-bold text-sm flex items-center gap-2"><UserRound className="w-4 h-4 text-[#B5838D]" /> {t("identityTitle")}</p>
            {identityOk && (
              <button data-testid="journal-identity-done" onClick={() => { saveIdentity(); setIdentityOpen(false); }}
                className="ml-auto text-xs font-semibold text-primary hover:text-[#1B4332] transition-[color] duration-200">{t("done")}</button>
            )}
          </div>
          <p className="text-xs text-muted-foreground -mt-1.5">{t("identitySub")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input data-testid="journal-guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} onBlur={saveIdentity} placeholder={t("namePh")} className="rounded-xl h-10" />
            <Input data-testid="journal-guest-email" type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} onBlur={saveIdentity} placeholder={t("emailPh")} className="rounded-xl h-10" />
          </div>
          <button data-testid="journal-subscribe-toggle" onClick={toggleSubscribe} disabled={subBusy}
            className={`w-full rounded-xl px-4 py-2.5 text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50 transition-[background-color,color] duration-200 ${subscribed ? "bg-primary/10 text-primary" : "bg-secondary hover:bg-secondary/70"}`}>
            <Bell className={`w-3.5 h-3.5 ${subscribed ? "fill-current" : ""}`} />
            {subscribed ? t("subOnTitle") : t("subOffTitle")}
          </button>
          <p className="text-[11px] text-muted-foreground leading-relaxed" data-testid="journal-sub-explain">{t("subExplain")}</p>
        </div>
        )}

        {journal.progress?.total_km && journal.progress.started ? (
          <TripProgressPanel progress={progress} moto={journal.travel_mode === "moto"} compact t={t} locale={locale}
            liveStatus={<LiveBar progress={progress} slim t={t} locale={locale} />} />
        ) : (
          <LiveBar progress={progress} t={t} locale={locale} />
        )}

        {journal.route_geometry?.length > 1 && (
          <div className={mapFull
            ? "fixed inset-0 z-[2000] bg-background"
            : "h-64 sm:h-72 rounded-2xl overflow-hidden shadow-sm relative"} data-testid="journal-map">
            <MapContainer ref={mapRef} bounds={L.latLngBounds(journal.route_geometry)} boundsOptions={{ padding: [30, 30] }} scrollWheelZoom={false} className="w-full h-full">
              <TileLayer url={CARTO_LIGHT} attribution='&copy; OSM &copy; CARTO' />
              <Polyline positions={journal.route_geometry} pathOptions={{ color: "#2D6A4F", weight: 4, opacity: journal.progress?.started && journal.progress.percent > 0 ? 0.3 : 0.85 }} />
              {journal.progress?.total_km ? <DrivenRoute geometry={journal.route_geometry} progress={progress} /> : null}
              <PhotoPins entries={logs} mediaUrl={mediaUrl} />
            </MapContainer>
            <button data-testid="journal-map-expand" onClick={toggleMapFull}
              title={mapFull ? t("mapSmaller") : t("mapBigger")}
              className="absolute top-3 right-3 z-[1000] w-10 h-10 rounded-full bg-white text-foreground shadow-lg flex items-center justify-center hover:bg-secondary active:scale-95 transition-[transform,background-color] duration-200">
              {mapFull ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          </div>
        )}

        {story && (
          <article className="bg-card rounded-2xl p-6" data-testid="journal-story">
            <h2 className="font-heading font-extrabold text-2xl tracking-tight mb-2">{tr?.story?.title || story.title}</h2>
            <p className="text-sm text-muted-foreground italic">{tr?.story?.intro || story.intro}</p>
            {storyOpen && (
              <>
                <div className="space-y-4 mt-4">
                  {story.days?.map((d) => (
                    <div key={d.day}>
                      <p className="text-xs tracking-[0.15em] uppercase font-semibold text-primary mb-1">{t("day")} {d.day}</p>
                      <p className="text-sm leading-relaxed">{tr?.story?.days?.[String(d.day)] || d.text}</p>
                    </div>
                  ))}
                </div>
                {story.outro && <p className="text-sm mt-4 font-medium">{tr?.story?.outro || story.outro}</p>}
              </>
            )}
            {((story.days || []).length > 0 || story.outro) && (
              <button data-testid="journal-story-toggle" onClick={() => setStoryOpen(!storyOpen)}
                className="mt-3 text-xs font-semibold text-primary hover:text-[#1B4332] flex items-center gap-1 transition-[color] duration-200">
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${storyOpen ? "rotate-180" : "-rotate-90"}`} />
                {storyOpen ? t("showLess") : ((story.days || []).length ? t("readMoreN", { n: story.days.length }) : t("readMore"))}
              </button>
            )}
          </article>
        )}

        <div data-testid="journal-log-days">
          <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground flex items-center gap-2 mb-4">
            <BookOpen className="w-4 h-4" /> {t("logTitle")} <span className="normal-case tracking-normal text-muted-foreground/70">· {t("newestFirst")}</span>
          </p>
          {(() => { const pc = logs.reduce((a, e) => a + entryImageIds(e).length, 0); return pc > 0 && (
            <div className="flex items-center gap-1 mb-4 bg-secondary rounded-full p-1 w-fit" data-testid="journal-view-toggle">
              <button data-testid="journal-view-log" onClick={() => setJournalView("log")}
                className={`rounded-full px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 transition-[background-color,color] duration-200 ${journalView === "log" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <BookOpen className="w-3.5 h-3.5" /> {t("viewStory")}
              </button>
              <button data-testid="journal-view-photos" onClick={() => setJournalView("photos")}
                className={`rounded-full px-4 py-1.5 text-xs font-bold flex items-center gap-1.5 transition-[background-color,color] duration-200 ${journalView === "photos" ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                <Images className="w-3.5 h-3.5" /> {t("viewPhotos")} <span className="font-mono font-normal">({pc})</span>
              </button>
            </div>
          ); })()}
          {journalView === "photos" ? (
            <JournalPhotoView groups={groups} other={other} mediaUrl={mediaUrl} t={t} locale={locale} dayLabel={dayLabel} />
          ) : (<>
          {visibleGroups.length === 0 && other.length === 0 && (
            <p className="text-sm text-muted-foreground bg-card rounded-2xl p-5">{t("noEntries")}</p>
          )}
          <div className="space-y-5">
            {visibleGroups.map(({ day, entries }, gi) => (
              <div key={day.day_number} className="relative pl-10" data-testid={`journal-day-${day.day_number}`}>
                <div className="absolute left-0 top-0.5 w-7 h-7 rounded-full bg-[#B5838D] text-white flex items-center justify-center font-heading font-bold text-xs shadow">
                  {day.day_number}
                </div>
                {gi < visibleGroups.length - 1 && <div className="absolute left-[13px] top-8 bottom-[-20px] w-px bg-border" />}
                <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">{day.date}</p>
                <p className="font-heading font-bold text-sm flex items-center gap-1.5">
                  {day.type === "stay" && <Palmtree className="w-3.5 h-3.5 text-accent" />}
                  {dayLabel(day)}
                </p>
                {entries.length > 0 && <div className="space-y-2.5 mt-3">{entries.map(renderEntry)}</div>}
                <DayComments comments={dayComments(day.day_number)} reactions={commentReactions} idKey={day.day_number}
                  myEmail={guestEmail.trim().toLowerCase()} onReact={reactComment} t={t} locale={locale}
                  onSubmit={submitComment(day.day_number, null)} label={t("dayCommentsLabel")}
                  replyOnly={entries.length > 0}
                  disabledHint={identityOk ? null : t("commentHint")} />
              </div>
            ))}
          </div>
          {other.length > 0 && (
            <div className="mt-6 space-y-2.5">
              <p className="text-[11px] tracking-[0.15em] uppercase font-semibold text-muted-foreground">{t("otherMoments")}</p>
              {[...other].reverse().map(renderEntry)}
            </div>
          )}
          </>)}
        </div>

        <SuggestionBox token={token} suggestions={journal.suggestions || []} days={days} t={t} locale={locale}
          guestName={guestName} guestEmail={guestEmail} identityOk={identityOk} saveIdentity={saveIdentity} onRefresh={load} />

        <div className="bg-card rounded-2xl p-6 space-y-4" data-testid="journal-feedback-form">
          <p className="font-heading font-bold flex items-center gap-2"><MessageCircleHeart className="w-5 h-5 text-[#B5838D]" /> {t("generalTitle")}</p>
          <form onSubmit={sendText} className="space-y-3">
            <Textarea data-testid="journal-feedback-input" value={text} onChange={(e) => setText(e.target.value)} rows={3}
              placeholder={t("feedbackPh")} className="rounded-xl" />
            <div className="flex flex-wrap items-center justify-between gap-3">
              <VoiceRecorder onRecorded={sendVoice} label={t("speakBtn")} />
              <Button data-testid="journal-feedback-submit" type="submit" disabled={sending || !text.trim()}
                className="rounded-full bg-primary hover:bg-[#1B4332]">
                <Send className="w-4 h-4 mr-1.5" /> {sending ? t("sending") : t("send")}
              </Button>
            </div>
          </form>
        </div>

        {feedback.length > 0 && (
          <div className="space-y-3" data-testid="journal-feedback-list">
            <p className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{t("generalList")} ({feedback.length})</p>
            {[...feedback].reverse().map((f) => (
              <div key={f.id} data-testid={`feedback-item-${f.id}`} className="bg-[#B5838D]/5 rounded-2xl p-4">
                <p className="text-sm leading-relaxed">{f.text}</p>
                <p className="text-xs text-muted-foreground mt-2 font-semibold flex items-center gap-1">
                  {f.source === "voice" && <Mic className="w-3 h-3" />} {f.author_name} · <span className="font-mono font-normal">{new Date(f.created_at).toLocaleString(locale, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                </p>
              </div>
            ))}
          </div>
        )}

        <footer className="text-center pb-8">
          <Link to="/" className="text-xs text-muted-foreground hover:text-primary transition-[color] duration-200">{t("footer")}</Link>
        </footer>
      </main>
    </div>
  );
}
